import {
  generators, macroPackageCaptureReplySchema, macroPackageCaptureRequestSchema, validateMacroPackageCaptureExchange,
  type MacroPackageCaptureReply, type MacroPackageCaptureRequest, type PlaycastMessage, type PlaycastMessageTarget,
} from '@playcastdotio/Messaging';
import type { IIpcService } from '../../services/ipc/IIpcService';
import type { RawPackageUploadClient } from '../../services/playjector/MacroPackageSocketLoader';

export type { RawPackageUploadClient } from '../../services/playjector/MacroPackageSocketLoader';
export type PlayjectorIpc = Pick<IIpcService, 'send'>;
export type MacroPackageCaptureMode = 'local' | 'campaign';
export interface MacroPackageCaptureRelayDeps {
  ipc: PlayjectorIpc; uploader: RawPackageUploadClient; mode: () => MacroPackageCaptureMode;
}

/** `mark` waits up to 3 s natively for positive offsets; `finalize` also drains the encoder. */
const REPLY_TIMEOUT_MS: Record<MacroPackageCaptureRequest['command'], number> = {
  start: 10_000, poll: 5_000, input: 10_000, mark: 10_000, finalize: 120_000, abort: 10_000,
  shortcut: 5_000,
  arm: 10_000, disarm: 10_000, toggle: 5_000,
};
const MAX_PENDING = 8;

interface PendingCapture { request: MacroPackageCaptureRequest; mode: MacroPackageCaptureMode;
  resolve(reply: MacroPackageCaptureReply): void; timer: ReturnType<typeof setTimeout> }

/**
 * Editor `system/macroPackageCapture` → Playjector `record/packageInputs`. One package may record per host;
 * replies are forwarded verbatim. A recording ends for the relay on `finalize` success or `abort` (a failed
 * finalize keeps the `.partial` for a retry). Legacy campaign Finalize uploads here; authoritative
 * seals expose the exact pin for the editor's explicit authenticated, observed upload operation.
 */
export class MacroPackageCaptureRelay {
  private activePackageId: string | null = null;
  private activeOwner: MacroPackageCaptureRequest | null = null;
  private activeMode: MacroPackageCaptureMode | null = null;
  private shortcutLease: MacroPackageCaptureRequest | null = null;
  private readonly pending = new Map<string, PendingCapture>();

  public constructor(private readonly deps: MacroPackageCaptureRelayDeps) {}

  public get active(): string | null { return this.activePackageId; }
  public get owned(): boolean {
    return !!this.activePackageId || !!this.shortcutLease || [...this.pending.values()].some(
      pending => (pending.request.command === 'shortcut' && pending.request.shortcut?.enabled)
        || (pending.request.command === 'poll' && !!pending.request.authoringProfile));
  }

  private mode(): MacroPackageCaptureMode { return this.activeMode ?? this.deps.mode(); }
  private clearActive(): void { this.activePackageId = null; this.activeOwner = null; this.activeMode = null; }

  /**
   * `mode` pins the recording's mode on `start` (local editor → `local`, remote admin peer → `campaign`);
   * without it the injected default applies.
   */
  public handle(raw: MacroPackageCaptureRequest, mode?: MacroPackageCaptureMode): Promise<MacroPackageCaptureReply> {
    const parsed = macroPackageCaptureRequestSchema.safeParse(raw);
    if (!parsed.success) return Promise.resolve(failure(raw, 'invalid_command'));
    const request = parsed.data;
    const requestMode = mode ?? this.mode();
    if (requestMode === 'local' && request.captureSource === 'editor'
      && ['start', 'arm', 'input'].includes(request.command)) {
      return Promise.resolve(failure(request, 'recording_local_editor_input_unsupported'));
    }
    const acquiresOwnership = request.command === 'start' || request.command === 'arm';
    if (acquiresOwnership && this.activePackageId && this.activePackageId !== request.packageId) {
      return Promise.resolve(failure(request, 'input_capture_busy'));
    }
    const receiptRecovery = this.activePackageId === null && ['finalize', 'abort'].includes(request.command);
    const lifecycleRecovery = this.activePackageId === null && request.command === 'poll' && !!request.authoringProfile;
    if (!acquiresOwnership && request.command !== 'shortcut' && this.activePackageId !== request.packageId
      && !receiptRecovery && !lifecycleRecovery) {
      return Promise.resolve(failure(request, 'input_capture_not_started'));
    }
    if (this.activeOwner && this.activePackageId === request.packageId
      && !!this.activeOwner.authoringProfile !== !!request.authoringProfile) {
      return Promise.resolve(failure(request, 'recording_protocol_mismatch'));
    }
    if (this.pending.has(request.requestId) || this.pending.size >= MAX_PENDING) {
      return Promise.resolve(failure(request, 'input_capture_request_busy'));
    }
    if (acquiresOwnership) {
      this.activePackageId = request.packageId; this.activeOwner = request; this.activeMode = mode ?? null;
    }
    return new Promise((resolve) => {
      const timer = setTimeout(() => this.settle(failure(request, 'input_capture_timeout')), REPLY_TIMEOUT_MS[request.command]);
      this.pending.set(request.requestId, { request, mode: requestMode, resolve, timer });
      try {
        this.deps.ipc.send(generators.macroPackageCapture.native('host', request, requestMode === 'local'));
      } catch {
        this.settle(failure(request, 'input_capture_send_failed'));
      }
    });
  }

  /** Consumes `record/packageInputs` replies; returns false for every other message. */
  public receive(message: PlaycastMessage<PlaycastMessageTarget>): boolean {
    const { target, action, isReply } = message.header;
    if (target !== 'record' || (action as string) !== 'packageInputs' || !isReply) return false;
    const parsed = macroPackageCaptureReplySchema.safeParse(message.body.message);
    if (parsed.success) {
      const pending = this.pending.get(parsed.data.requestId);
      if (!pending || pending.request.packageId !== parsed.data.packageId) return true;
      const exchange = validateMacroPackageCaptureExchange(pending.request, parsed.data);
      this.settle(exchange.ok ? parsed.data : failure(pending.request, exchange.issues[0] ?? 'recording_integrity_failed'));
    } else {
      // A correlated native reply is still an answer. Surface a bounded integrity error now
      // instead of discarding it and misreporting an input_capture_timeout ten seconds later.
      const raw = message.body.message as Record<string, unknown> | undefined;
      const requestId = typeof raw?.requestId === 'string' ? raw.requestId : '';
      const packageId = typeof raw?.packageId === 'string' ? raw.packageId : '';
      const pending = this.pending.get(requestId);
      if (pending && pending.request.packageId === packageId) {
        this.settle(failure(pending.request, 'recording_integrity_failed', 'invalid_native_capture_reply'));
      }
    }
    return true;
  }

  /** Owner gone: abort the active recording (best effort; the native lease also expires without polls). */
  public release(): void {
    const shortcut = this.shortcutLease ?? [...this.pending.values()].find(
      pending => pending.request.command === 'shortcut' && pending.request.shortcut?.enabled)?.request;
    this.shortcutLease = null;
    if (shortcut) {
      try { this.deps.ipc.send(generators.macroPackageCapture.native('host', { ...shortcut,
        requestId: crypto.randomUUID(), shortcut: { enabled: false } }, this.mode() === 'local')); }
      catch { /* native shortcut lease also expires */ }
    }
    const owner = this.activeOwner;
    const localEditor = this.mode() === 'local';
    this.clearActive();
    for (const pending of [...this.pending.values()]) this.settle(failure(pending.request, 'input_capture_released'));
    if (!owner) return;
    const { requestId: _previous, mark: _mark, finalize: _finalize, expectedHost: _host,
      armSpec: _arm, toggle: _toggle, ...base } = owner;
    if (owner.authoringProfile) {
      // This is the authenticated owner-loss signal, not a browser recording boundary. Native Disarm
      // cancels a merely armed take or closes/finalizes an active take at the Host's current QPC time.
      try { this.deps.ipc.send(generators.macroPackageCapture.native('host', { ...base,
        requestId: crypto.randomUUID(), command: 'disarm', afterSeq: -1 }, localEditor)); }
      catch { /* native owner lease remains the final fail-safe */ }
      return;
    }
    try {
      this.deps.ipc.send(generators.macroPackageCapture.native('host', { ...base, requestId: crypto.randomUUID(),
        command: 'abort', afterSeq: -1 }, localEditor));
    } catch { /* legacy native lease expiry discards it */ }
  }

  private settle(reply: MacroPackageCaptureReply): void {
    const pending = this.pending.get(reply.requestId);
    if (!pending || (pending.request.packageId !== reply.packageId && reply.packageId !== '')) return;
    clearTimeout(pending.timer);
    this.pending.delete(reply.requestId);
    this.track(pending.request, reply, pending.mode);
    pending.resolve(reply);
  }

  private track(request: MacroPackageCaptureRequest, reply: MacroPackageCaptureReply, requestMode: MacroPackageCaptureMode): void {
    if (request.command === 'shortcut' && reply.success) {
      this.shortcutLease = request.shortcut?.enabled ? request : null;
      return;
    }
    let ownsActive = request.packageId === this.activePackageId;
    if (!ownsActive && request.command === 'poll' && request.authoringProfile && reply.success && reply.lifecycle
      && !['idle', 'finalized', 'failed'].includes(reply.lifecycle.phase)) {
      this.activePackageId = request.packageId; this.activeOwner = request; this.activeMode = requestMode; ownsActive = true;
    }
    if (ownsActive && ['start', 'arm'].includes(request.command) && !reply.success
      && ['input_capture_timeout', 'input_capture_send_failed'].includes(reply.error ?? '')) {
      // Arm uncertainty must retain identity for lifecycle reconciliation. Legacy Start has no such
      // lifecycle, so release its exact package before a late success can outlive the editor.
      if (request.command === 'start') this.release();
      return;
    }
    const lifecycleEnded = !!reply.lifecycle && ['idle', 'finalized', 'failed'].includes(reply.lifecycle.phase);
    const ended = request.command === 'abort' || (request.command === 'finalize' && reply.success && reply.finalized)
      || (['start', 'arm'].includes(request.command) && !reply.success) || lifecycleEnded;
    if (ownsActive && ended) this.clearActive();
    if (requestMode !== 'campaign' || !reply.success || !reply.finalized) return;
    if (request.command === 'finalize' && request.finalize) { this.upload(request.packageId, request.finalize.campaignId); return; }
    // Host-authoritative Stop exposes the immutable pin. The editor starts its explicit
    // upload operation with a campaign grant and observes/retries that exact operation.
  }

  private upload(packageId: string, campaignId: string): void {
    void this.deps.uploader.upload(packageId, campaignId).then((result) => {
      if (!result.ok) console.warn('[MacroPackageCaptureRelay] Raw package upload failed', { packageId, error: result.error });
    }, (error: unknown) => console.warn('[MacroPackageCaptureRelay] Raw package upload threw', { packageId, error }));
  }
}

function failure(request: Partial<MacroPackageCaptureRequest>, error: string, diagnostic?: string): MacroPackageCaptureReply {
  return { requestId: String(request.requestId ?? '').slice(0, 128) || 'invalid', packageId: String(request.packageId ?? ''),
    success: false, events: [], more: false, stopped: true, error, ...(diagnostic ? { diagnostic } : {}) };
}
